"use client";

import React, { useState, useRef, useEffect, useCallback } from "react";
import { getCMSImageURL } from "@/helpers/CMSImageURL";
import classNames from "classnames";

// Image cache to prevent redundant requests
const imageCache = new Map();
const DEFAULT_CDN_OPTIONS = {};

const ProgressiveImage = ({
  imageData,
  alt,
  className,
  style,
  onLoad,
  onError,
  priority = false,
  sizes = "100vw",
  quality = 75,
  disableSkeleton = false,
  cdnOptions = DEFAULT_CDN_OPTIONS,
  ...props
}) => {
  const [currentSrc, setCurrentSrc] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const imgRef = useRef(null);
  const observerRef = useRef(null);
  const abortController = useRef(null);

  const { src, aspectRatio } = imageData || {};
  const hasHighRes = !!src;

  // Reset state when imageData changes (e.g. device switch after hydration)
  useEffect(() => {
    if (hasHighRes) {
      setCurrentSrc(null);
      setIsLoading(true);
      setHasError(false);
    }
  }, [src]);

  const preloadImage = useCallback((src) => {
    const cacheKey = getCMSImageURL(src, cdnOptions);

    // Return cached promise if exists
    if (imageCache.has(cacheKey)) {
      return imageCache.get(cacheKey);
    }

    const promise = new Promise((resolve, reject) => {
      const img = new Image();
      img.decoding = 'async';
      img.loading = 'eager';
      img.crossOrigin = 'anonymous';

      const cleanup = () => {
        img.onload = null;
        img.onerror = null;
      };

      img.onload = () => {
        cleanup();
        resolve(src);
      };

      img.onerror = () => {
        cleanup();
        imageCache.delete(cacheKey);
        reject(new Error(`Failed to load image: ${cacheKey}`));
      };

      // Use requestIdleCallback for better performance
      if ('requestIdleCallback' in window && !priority) {
        window.requestIdleCallback(() => {
          img.src = cacheKey;
        }, { timeout: 2000 });
      } else {
        img.src = cacheKey;
      }
    });

    // Cache the promise
    imageCache.set(cacheKey, promise);
    return promise;
  }, [priority, cdnOptions]);

  const loadNextImage = useCallback(async () => {
    if (!imageData || !hasHighRes) return;

    try {
      // Load the best available source
      const imageSrc = src;
      if (imageSrc) {
        await preloadImage(imageSrc);
        setCurrentSrc(getCMSImageURL(imageSrc, cdnOptions));
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    } catch (error) {
      console.warn('Image loading error:', error);
      setHasError(true);
      setIsLoading(false);
    }
  }, [imageData, src, hasHighRes, preloadImage, cdnOptions]);

  useEffect(() => {
    if (!imageData || hasError || !hasHighRes) return;

    const element = imgRef.current;
    if (!element) return;

    if (priority) {
      loadNextImage();
      return;
    }

    if ('IntersectionObserver' in window) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              loadNextImage();
              observerRef.current?.unobserve(element);
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.1
        }
      );

      observerRef.current.observe(element);
    } else {
      loadNextImage();
    }

    return () => {
      observerRef.current?.disconnect();
      if (abortController.current) {
        abortController.current.abort();
      }
    };
  }, [imageData, src, priority, hasError, hasHighRes]);

  const handleLoad = useCallback(() => {
    onLoad?.();
  }, [onLoad]);

  const handleError = useCallback(() => {
    setHasError(true);
    setIsLoading(false);
    onError?.();
  }, [onError]);

  if (!imageData || hasError) {
    return (
      <div
        className={classNames(
          "flex items-center justify-center bg-gray-800 text-gray-400",
          className
        )}
        style={{
          aspectRatio: aspectRatio || "16/9",
          ...style
        }}
        {...props}
      >
        {hasError ? "Failed to load" : "No image"}
      </div>
    );
  }


  return (
    <div
      ref={imgRef}
      className={classNames("relative overflow-hidden", className)}
      style={{
        aspectRatio: aspectRatio || "16/9",
        ...style
      }}
      {...props}
    >
      {/* Skeleton placeholder — shown until image loads */}
      {isLoading && !disableSkeleton && (
        <div
          className="absolute inset-0 w-full h-full bg-gray-800 animate-pulse"
          style={{ zIndex: 1 }}
        />
      )}

      {/* Main Image — progressive JPEGs render progressively by default */}
      {currentSrc && (
        <img
          src={currentSrc}
          alt={alt}
          className={classNames(
            "absolute inset-0 w-full h-full object-cover transition-opacity duration-500 ease-out",
            isLoading ? "opacity-0" : "opacity-100"
          )}
          style={{ zIndex: 2 }}
          decoding="async"
          sizes={sizes}
          onLoad={handleLoad}
          onError={handleError}
        />
      )}
    </div>
  );
};

export default ProgressiveImage;